import { FormEvent, useEffect, useMemo, useState } from 'react';
import type { Session } from '@supabase/supabase-js';
import { clampDisplaySeconds, parseAdMeta, serializeAdMeta, type AdSize } from '../../../lib/adMeta';
import { supabase } from '../../../lib/supabase';

type CmsPost = {
  id: string;
  title: string;
  body: string | null;
  image_url: string | null;
  link_url: string | null;
  is_published: boolean;
  sort_order: number;
  created_at: string;
};

type EditorState = {
  id?: string;
  title: string;
  note: string;
  displayText: string;
  phoneNumber: string;
  displaySeconds: number;
  bannerSize: AdSize;
  imageUrl: string;
  linkUrl: string;
  isPublished: boolean;
  sortOrder: number;
};

const emptyEditor: EditorState = {
  title: '',
  note: '',
  displayText: '',
  phoneNumber: '',
  displaySeconds: 5,
  bannerSize: 'medium',
  imageUrl: '',
  linkUrl: '',
  isPublished: true,
  sortOrder: 0,
};

const sizeLabels: Record<AdSize, string> = {
  small: 'صغير — بانر نحيف',
  medium: 'متوسط — مقترح',
  large: 'كبير — مساحة أوضح',
};

export default function AdsPanel({ session }: { session: Session }) {
  const [message, setMessage] = useState('');
  const [posts, setPosts] = useState<CmsPost[]>([]);
  const [editor, setEditor] = useState<EditorState>(emptyEditor);
  const [mediaFile, setMediaFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const editing = useMemo(() => Boolean(editor.id), [editor.id]);

  useEffect(() => {
    void loadPosts();
  }, []);

  async function loadPosts() {
    if (!supabase) return;
    const { data, error } = await supabase
      .from('cms_posts')
      .select('*')
      .order('sort_order', { ascending: false })
      .order('created_at', { ascending: false });
    if (error) return setMessage(`تعذر تحميل الإعلانات: ${error.message}`);
    setPosts(data || []);
  }

  async function uploadMedia(file: File) {
    if (!supabase) return null;
    const maxBytes = 50 * 1024 * 1024;
    if (file.size > maxBytes) throw new Error('حجم الملف أكبر من 50MB. صغّر الفيديو أو الصورة ثم جرّب مرة ثانية.');
    const ext = file.name.split('.').pop() || 'jpg';
    const path = `${session.user.id}/${Date.now()}-${crypto.randomUUID()}.${ext}`;
    const { error } = await supabase.storage.from('cms-media').upload(path, file, { cacheControl: '3600', upsert: false });
    if (error) throw error;
    return supabase.storage.from('cms-media').getPublicUrl(path).data.publicUrl;
  }

  async function savePost(event: FormEvent) {
    event.preventDefault();
    if (!supabase || !editor.title.trim()) return;
    if (!mediaFile && !editor.imageUrl) return setMessage('اختر صورة أو فيديو للإعلان أولاً.');
    setSaving(true);
    setMessage('');
    try {
      let imageUrl = editor.imageUrl || null;
      if (mediaFile) imageUrl = await uploadMedia(mediaFile);
      const payload = {
        title: editor.title.trim(),
        body: serializeAdMeta({
          note: editor.note,
          displayText: editor.displayText,
          phoneNumber: editor.phoneNumber,
          displaySeconds: editor.displaySeconds,
          size: editor.bannerSize,
        }),
        image_url: imageUrl,
        link_url: editor.linkUrl.trim() || null,
        is_published: editor.isPublished,
        sort_order: Number(editor.sortOrder) || 0,
        updated_at: new Date().toISOString(),
      };
      const result = editor.id
        ? await supabase.from('cms_posts').update(payload).eq('id', editor.id)
        : await supabase.from('cms_posts').insert(payload);
      if (result.error) throw result.error;
      setMessage(editor.id ? 'تم تعديل الإعلان بنجاح.' : 'تم نشر الإعلان. راح يظهر بنفس مكان الإعلانات ويتبدل حسب المدة.');
      setEditor(emptyEditor);
      setMediaFile(null);
      await loadPosts();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'صار خطأ أثناء الحفظ.');
    } finally {
      setSaving(false);
    }
  }

  function editPost(post: CmsPost) {
    const meta = parseAdMeta(post.body);
    setEditor({
      id: post.id,
      title: post.title,
      note: meta.note,
      displayText: meta.displayText,
      phoneNumber: meta.phoneNumber,
      displaySeconds: meta.displaySeconds,
      bannerSize: meta.size,
      imageUrl: post.image_url || '',
      linkUrl: post.link_url || '',
      isPublished: post.is_published,
      sortOrder: post.sort_order || 0,
    });
    setMediaFile(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  async function deletePost(id: string) {
    if (!supabase || !window.confirm('متأكد تريد حذف هذا الإعلان؟')) return;
    const { error } = await supabase.from('cms_posts').delete().eq('id', id);
    if (error) return setMessage(error.message);
    if (editor.id === id) setEditor(emptyEditor);
    await loadPosts();
  }

  return (
    <div className="grid lg:grid-cols-[420px_1fr] gap-6">
      <section className="rounded-3xl border border-white/10 bg-white/5 p-5 h-fit lg:sticky lg:top-36">
        <h2 className="font-bold text-lg mb-2">{editing ? 'تعديل الإعلان' : 'إعلان جديد'}</h2>
        <p className="text-xs text-gray-400 leading-6 mb-5">كل الإعلانات تظهر في نفس المساحة وتتبدل حسب مدة الظهور التي تختارها لكل إعلان.</p>
        <form onSubmit={savePost} className="space-y-4">
          <div><label className="text-sm text-gray-300 block mb-2">اسم الإعلان (للإدارة فقط) *</label><input value={editor.title} onChange={(e) => setEditor({ ...editor, title: e.target.value })} className="admin-input" required /></div>
          <div><label className="text-sm text-gray-300 block mb-2">النص الظاهر للزائر (اختياري)</label><textarea value={editor.displayText} onChange={(e) => setEditor({ ...editor, displayText: e.target.value })} className="admin-input min-h-20 resize-y" placeholder="مثلاً: خصم خاص على تأسيس اللوحات الكهربائية" /></div>
          <div><label className="text-sm text-gray-300 block mb-2">ملاحظة داخلية (لا تظهر للزائر)</label><textarea value={editor.note} onChange={(e) => setEditor({ ...editor, note: e.target.value })} className="admin-input min-h-20 resize-y" /></div>
          <div>
            <label className="text-sm text-gray-300 block mb-2">صورة أو فيديو للإعلان</label>
            <input type="file" accept="image/*,video/mp4,video/webm,video/quicktime" onChange={(e) => setMediaFile(e.target.files?.[0] || null)} className="block w-full text-sm text-gray-300" />
            <p className="text-[11px] text-gray-500 mt-2">صور أو MP4 / WebM / MOV، بحد أقصى 50MB.</p>
            {editor.imageUrl && !mediaFile && (/\.(mp4|webm|mov|m4v|ogg)(\?|$)/i.test(editor.imageUrl) ? <video src={editor.imageUrl} className="mt-3 rounded-xl w-full max-h-40 object-cover bg-black" muted controls playsInline /> : <img src={editor.imageUrl} className="mt-3 rounded-xl w-full max-h-40 object-cover" alt="معاينة الإعلان" />)}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-sm text-gray-300 block mb-2">مدة الظهور (ثانية)</label><input type="number" min={2} max={60} value={editor.displaySeconds} onChange={(e) => setEditor({ ...editor, displaySeconds: clampDisplaySeconds(e.target.value) })} className="admin-input" /></div>
            <div><label className="text-sm text-gray-300 block mb-2">حجم الإعلان</label><select value={editor.bannerSize} onChange={(e) => setEditor({ ...editor, bannerSize: e.target.value as AdSize })} className="admin-input"><option value="small">{sizeLabels.small}</option><option value="medium">{sizeLabels.medium}</option><option value="large">{sizeLabels.large}</option></select></div>
          </div>
          <div><label className="text-sm text-gray-300 block mb-2">رابط عند الضغط (اختياري)</label><input type="url" value={editor.linkUrl} onChange={(e) => setEditor({ ...editor, linkUrl: e.target.value })} className="admin-input" placeholder="https://..." /></div>
          <div><label className="text-sm text-gray-300 block mb-2">رقم هاتف (اختياري)</label><input type="tel" value={editor.phoneNumber} onChange={(e) => setEditor({ ...editor, phoneNumber: e.target.value })} className="admin-input" placeholder="07xx xxx xxxx" dir="ltr" /></div>
          <div><label className="text-sm text-gray-300 block mb-2">ترتيب الدوران — الرقم الأعلى يبدأ أولاً</label><input type="number" value={editor.sortOrder} onChange={(e) => setEditor({ ...editor, sortOrder: Number(e.target.value) })} className="admin-input" /></div>
          <label className="flex items-center gap-3 cursor-pointer"><input type="checkbox" checked={editor.isPublished} onChange={(e) => setEditor({ ...editor, isPublished: e.target.checked })} className="w-5 h-5 accent-[#F5A623]" /><span>إعلان مرئي للزوار</span></label>
          {message && <p className="text-sm text-[#F5A623] leading-6">{message}</p>}
          <div className="flex gap-2"><button disabled={saving} className="flex-1 rounded-xl bg-[#F5A623] text-black font-bold py-3 disabled:opacity-50">{saving ? 'جاري الحفظ...' : editing ? 'حفظ التعديل' : 'نشر الإعلان'}</button>{editing && <button type="button" onClick={() => { setEditor(emptyEditor); setMediaFile(null); }} className="rounded-xl border border-white/10 px-4">إلغاء</button>}</div>
        </form>
      </section>

      <section>
        <div className="flex items-center justify-between mb-4"><h2 className="font-bold text-lg">الإعلانات الحالية</h2><span className="text-xs text-gray-400">{posts.length} إعلان</span></div>
        <div className="space-y-4">
          {posts.length === 0 && <div className="rounded-2xl border border-dashed border-white/10 p-10 text-center text-gray-500">ما عندك إعلانات لحد الآن.</div>}
          {posts.map((post) => {
            const meta = parseAdMeta(post.body);
            return (
              <article key={post.id} className="rounded-2xl border border-white/10 bg-white/5 overflow-hidden sm:flex">
                {post.image_url && (/\.(mp4|webm|mov|m4v|ogg)(\?|$)/i.test(post.image_url) ? <video src={post.image_url} className="w-full sm:w-44 h-36 object-cover bg-black" muted playsInline /> : <img src={post.image_url} className="w-full sm:w-44 h-36 object-cover" alt={post.title} />)}
                <div className="p-4 flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3"><div><h3 className="font-bold">{post.title}</h3><div className="flex flex-wrap gap-2 mt-2"><span className={`inline-block text-[11px] rounded-full px-2 py-1 ${post.is_published ? 'bg-green-500/15 text-green-300' : 'bg-gray-500/15 text-gray-400'}`}>{post.is_published ? 'ظاهر' : 'مخفي'}</span><span className="inline-block text-[11px] rounded-full px-2 py-1 bg-[#F5A623]/10 text-[#F5A623]">{meta.displaySeconds} ثواني</span><span className="inline-block text-[11px] rounded-full px-2 py-1 bg-white/10 text-gray-300">{sizeLabels[meta.size]}</span></div></div><span className="text-xs text-gray-500">ترتيب {post.sort_order}</span></div>
                  {meta.displayText && <p className="text-sm text-white/80 mt-3 line-clamp-2 whitespace-pre-line">{meta.displayText}</p>}
                  {meta.note && <p className="text-xs text-gray-500 mt-2 line-clamp-2 whitespace-pre-line">ملاحظة: {meta.note}</p>}
                  <div className="flex gap-2 mt-4"><button onClick={() => editPost(post)} className="rounded-lg bg-[#F5A623]/15 text-[#F5A623] px-3 py-2 text-sm">تعديل</button><button onClick={() => deletePost(post.id)} className="rounded-lg bg-red-500/10 text-red-300 px-3 py-2 text-sm">حذف</button></div>
                </div>
              </article>
            );
          })}
        </div>
      </section>
    </div>
  );
}
